import type { DragEvent } from 'react';
import { useCallback, useRef, useState } from 'react';

/** True when the drag carries files (not text or a link dragged from the page). */
function draggingFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

export interface FileDropBindings {
  onDragEnter: (event: DragEvent) => void;
  onDragOver: (event: DragEvent) => void;
  onDragLeave: (event: DragEvent) => void;
  onDrop: (event: DragEvent) => void;
}

/**
 * Turn an element into a file drop zone. Spread `bindings` onto the element and
 * use `isDragging` to show a "drop to attach" state.
 *
 * `dragenter`/`dragleave` fire for every child the pointer crosses, so a plain
 * boolean flickers off whenever the drag passes over a nested element. A depth
 * counter tracks how many nested elements the drag is inside, and the zone only
 * reads as inactive once it has left all of them.
 *
 * Only file drags are claimed: dragging selected text or a link still behaves
 * normally. When `enabled` is false the zone ignores drags entirely, so the
 * browser's default (opening the file in the tab) is not suppressed either.
 */
export default function useFileDrop(
  onFiles: (files: File[]) => void,
  enabled = true,
): { isDragging: boolean; bindings: FileDropBindings } {
  const [isDragging, setIsDragging] = useState(false);
  const depth = useRef(0);

  const onDragEnter = useCallback(
    (event: DragEvent) => {
      if (!enabled || !draggingFiles(event)) return;
      event.preventDefault();
      depth.current += 1;
      setIsDragging(true);
    },
    [enabled],
  );

  const onDragOver = useCallback(
    (event: DragEvent) => {
      if (!enabled || !draggingFiles(event)) return;
      // Required: without it the browser refuses the drop.
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    },
    [enabled],
  );

  const onDragLeave = useCallback(
    (event: DragEvent) => {
      if (!enabled || !draggingFiles(event)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setIsDragging(false);
    },
    [enabled],
  );

  const onDrop = useCallback(
    (event: DragEvent) => {
      if (!enabled || !draggingFiles(event)) return;
      event.preventDefault();
      depth.current = 0;
      setIsDragging(false);
      const files = Array.from(event.dataTransfer.files ?? []);
      if (files.length > 0) onFiles(files);
    },
    [enabled, onFiles],
  );

  return { isDragging, bindings: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
